import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Lock } from "lucide-react";
import ForaLogo from "../components/ForaLogo";
import CoverageMap from "../components/CoverageMap";

const REPORTS = [
    {
        id: "operations",
        eyebrow: "Operations",
        title: "Operations Build & Setup.",
        owners: "Tanja Krasić · COO",
        period: "Setup phase",
        forward: "May – Jul 2026",
        accent: "var(--fora-red-1)",
        snapshot: [
            { n: "8", label: "Workstreams" },
            { n: "2", label: "Owners" },
            { n: "3", label: "Phase buckets" },
        ],
        scope: "Org & Governance · Processes · BO Admin · Tooling · Reporting",
    },
    {
        id: "business",
        eyebrow: "Business",
        title: "Commercial and Business Progress.",
        owners: "Marko Erkić · CBO",
        period: "Mar – Apr 2026",
        forward: "May – Jul 2026",
        accent: "var(--fora-red-1)",
        snapshot: [
            { n: "23", label: "Deliverables produced" },
            { n: "5M", label: "EUR 2026 target" },
            { n: "Q3", label: "Push quarter" },
        ],
        scope: "HubSpot CRM · GTM strategy · Sales pipeline · CCHBC activation · Investor framework",
    },
    {
        id: "product-it",
        eyebrow: "Product & IT",
        title: "Product and IT Progress.",
        owners: "Franjo Vučić",
        period: "Mar – Apr 2026",
        forward: "May – Jul 2026",
        accent: "var(--fora-red-1)",
        snapshot: [
            { n: "6", label: "Strategic priorities" },
            { n: "2", label: "KPI families" },
            { n: "+8", label: "Roles needed" },
        ],
        scope: "MyFora · Digital Onboarding · Pay at Table · Adyen · CCHBC standardization",
    },
];

const Hub = () => {
    // Remove the platform "Made with Emergent" badge from the DOM.
    useEffect(() => {
        const badge = document.getElementById("emergent-badge");
        if (badge) badge.remove();
    }, []);

    return (
        <div
            className="min-h-screen bg-black text-white relative overflow-hidden"
            data-testid="hub-page"
        >
            <div className="absolute inset-0 fora-pattern opacity-90" />
            <div className="absolute -top-40 -right-40 w-[680px] h-[680px] rounded-full bg-[var(--fora-red-1)] opacity-20 blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full bg-[var(--fora-red-2)] opacity-15 blur-3xl" />

            <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-12 lg:py-20">
                {/* HEADER */}
                <header className="flex items-center justify-between mb-16 lg:mb-24">
                    <div className="flex items-center gap-3">
                        <ForaLogo
                            variant="wordmark"
                            size="1.75rem"
                            color="white"
                        />
                        <span className="hidden sm:inline-block text-[11px] tracking-[0.22em] uppercase text-white/55 font-semibold border-l border-white/15 pl-3">
                            Solutions
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-white/55 font-semibold">
                        <Lock className="w-3.5 h-3.5" />
                        Confidential · Internal
                    </div>
                </header>

                {/* HERO */}
                <div className="max-w-4xl">
                    <div className="flex items-center gap-3 rise">
                        <span className="red-dot" />
                        <span className="section-num text-white/80">
                            FORA · Master F&amp;B Progress Reports
                        </span>
                    </div>
                    <h1
                        className="rise d1 mt-6 font-[800] tracking-[-0.04em] leading-[0.95] text-4xl sm:text-6xl lg:text-[88px]"
                        data-testid="hub-title"
                    >
                        Three reports.
                        <br />
                        One direction.
                    </h1>
                    <p className="rise d2 mt-6 max-w-2xl text-sm sm:text-lg text-white/80 leading-relaxed">
                        Internal progress reports prepared for{" "}
                        <span className="text-white font-semibold">
                            Ivan Brkić · Managing Director
                        </span>{" "}
                        and the Master F&amp;B executive team — covering{" "}
                        <span className="text-white font-semibold">Product &amp; IT</span>,{" "}
                        <span className="text-white font-semibold">Business</span>, and{" "}
                        <span className="text-white font-semibold">Operations</span>.
                        Reporting period{" "}
                        <span className="text-white font-semibold">
                            March – April 2026
                        </span>
                        , forward view{" "}
                        <span className="text-white font-semibold">
                            May – July 2026
                        </span>
                        .
                    </p>
                </div>

                {/* REPORT CARDS */}
                <div
                    className="rise d3 mt-16 lg:mt-24 grid grid-cols-1 lg:grid-cols-3 gap-5"
                    data-testid="hub-cards"
                >
                    {REPORTS.map((r) => (
                        <Link
                            key={r.id}
                            to={`/${r.id}`}
                            className="group relative bg-black/60 backdrop-blur border border-white/15 hover:border-[var(--fora-red-1)] transition-all duration-300 hover:-translate-y-1 p-5 sm:p-8 flex flex-col"
                            data-testid={`hub-card-${r.id}`}
                        >
                            <div className="flex items-center justify-between mb-5">
                                <div className="flex items-center gap-2">
                                    <span className="red-dot" />
                                    <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                                        {r.eyebrow}
                                    </span>
                                </div>
                                <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-[var(--fora-red-1)] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                            </div>

                            <h2 className="font-[800] text-2xl sm:text-3xl tracking-[-0.03em] leading-[1.05] mb-5 lg:min-h-[3.5em]">
                                {r.title}
                            </h2>

                            <div className="grid grid-cols-3 gap-3 mb-6 pb-6 border-b border-white/10 min-h-[78px]">
                                {r.snapshot.map((s, i) => (
                                    <div key={i}>
                                        <div className="font-[800] text-2xl tracking-[-0.04em] leading-none">
                                            {s.n}
                                            <span className="text-[var(--fora-red-1)]">.</span>
                                        </div>
                                        <div className="mt-1 text-[10px] text-white/55 leading-tight min-h-[24px]">
                                            {s.label}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="space-y-3 text-xs flex-1">
                                <div>
                                    <div className="text-[9px] font-bold tracking-[0.22em] uppercase text-white/45 mb-1">
                                        Owner
                                    </div>
                                    <div className="text-white/85 font-semibold">
                                        {r.owners}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <div className="text-[9px] font-bold tracking-[0.22em] uppercase text-white/45 mb-1">
                                            Reporting
                                        </div>
                                        <div className="text-white/75">{r.period}</div>
                                    </div>
                                    <div>
                                        <div className="text-[9px] font-bold tracking-[0.22em] uppercase text-white/45 mb-1">
                                            Forward
                                        </div>
                                        <div className="text-white/75">{r.forward}</div>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[9px] font-bold tracking-[0.22em] uppercase text-white/45 mb-1">
                                        Scope
                                    </div>
                                    <div className="text-white/65 leading-relaxed text-[11px]">
                                        {r.scope}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                                <span className="text-[10px] tracking-[0.22em] uppercase text-white/55 font-semibold">
                                    Open report
                                </span>
                                <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-[var(--fora-red-1)] font-bold group-hover:gap-3 transition-all">
                                    Read · PDF
                                    <ArrowUpRight className="w-3.5 h-3.5" />
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>

                {/* COVERAGE MAP — Venn diagram */}
                <CoverageMap />

                {/* FOOTER NOTE */}
                <div className="mt-16 lg:mt-24 pt-8 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-[11px] text-white/45 tracking-[0.04em]">
                    <div data-testid="hub-footer-info">
                        <span className="text-white/85 font-semibold">
                            Master F&amp;B d.o.o.
                        </span>{" "}
                        · Vlaška 79, Zagreb · FORA is a brand by Master F&amp;B
                    </div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--fora-red-1)] font-semibold">
                        Last updated · May 20, 2026
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Hub;
